const uniq = require('lodash/uniq');
const uniqBy = require('lodash/uniqBy');
const momentTimezone = require('moment-timezone');

// The three response lists on an incident — responded, standby, cancelled — are
// mutually exclusive. Riding a truck and being stood down are contradictory
// states, and payroll prices them differently, so a member in two of them at once
// gets paid twice (#1458).
//
// The Responses tab enforces this on every edit (handleUserChange), but the IAR
// import didn't: it merged each list against itself only —
//
//     setValue('users',   uniq([...users,   ...newUsers]));
//     setValue('standby', uniq([...standby, ...newStandby]));
//
// — so a member already in `users` who came back classified "standby" ended up in
// both. JRFD has 18 such member-incidents, each paid salary + the standby credit
// for a single call.
//
// Named `responders` rather than `users` because the caller supplies that list
// under its own key: incidents use `users`, training uses `members`.
const LISTS = ['responders', 'standby', 'cancelled'];
const DEFAULT_IAR_IGNORED_RESPONSES = ['unavailable', 'not responding', 'not available', 'unable', 'call complete'];

// Lives in `common` rather than the app because there are now TWO callers and
// they must not drift (#1665): the officer-driven ImportDialog, and the
// unattended IAR poller in the api. A classification rule that differs between
// "the officer imported it" and "the poller imported it" would be invisible
// until payroll disagreed with itself.

// IaR reports a free-text "Responding to" per member ("Sta (From Home)",
// "Station <30", "Standby", "Cancelled"). Ignore phrases are case-insensitive
// literal substrings, configurable per agency in settings/general. Missing
// settings use the defaults; an explicit empty array disables ignoring.
// Unknown values (including "Not Selected") still count as responding.
const classifyIarResponse = (response, ignoredResponses = DEFAULT_IAR_IGNORED_RESPONSES) => {
    const value = String(response ?? '');
    const patterns = Array.isArray(ignoredResponses) ? ignoredResponses : DEFAULT_IAR_IGNORED_RESPONSES;
    if (patterns.some(pattern => typeof pattern === 'string' && pattern.trim()
        && value.toLowerCase().includes(pattern.trim().toLowerCase()))) return 'ignore';
    if (/cancelled/i.test(value)) return 'cancelled';
    if (/standby/i.test(value)) return 'standby';
    return 'responders';
};

const responseUid = item => {
    const uid = item?.user?.uid || item?.user;
    return typeof uid === 'string' && uid ? uid : null;
};

// Exports list taps oldest first. Keep every tap for the snapshot; only the
// latest per member (or unmatched name) drives attendance and the selection UI.
const latestIarResponses = (responses = []) => uniqBy([...responses].reverse(), item =>
    responseUid(item) || JSON.stringify([item.raw?.firstName, item.raw?.lastName])
);

const iarResponseSnapshot = (responses = [], ignoredResponses) => responses.map(item => {
    const uid = responseUid(item);
    const {date, time, firstName, lastName} = item.raw || {};
    const parsed = date && time ? momentTimezone.tz(
        `${date} ${time}`,
        ['MM/DD/YYYY HH:mm:ss', 'MM/DD/YYYY HH:mm', 'MM/DD/YYYY h:mm:ss A', 'MM/DD/YYYY h:mm A'],
        true,
        'America/Vancouver'
    ) : null;
    return {
        uid,
        ...!uid && {name: [firstName, lastName].filter(Boolean).join(' ')},
        response: item.response ?? '',
        // Missing or malformed timestamps must not invent a response time or
        // discard the row. Null is also safe to write to Firestore.
        at: parsed?.isValid() ? parsed.toDate() : null,
        classification: classifyIarResponse(item.response, ignoredResponses)
    };
});

// Fold a batch of freshly imported classifications into the lists already on the
// record. An imported classification WINS — importing someone as standby moves
// them off the responder list rather than adding a second entry. Members the
// import doesn't mention are left exactly where they are, so re-importing a
// subset never disturbs everyone else.
//
// Imported-wins is safe for the unattended poller too, because it only ever
// touches PENDING_IMPORT incidents and the first officer save flips an incident
// to IN_PROGRESS (src/pages/Incident.js). So a list the poller can still write
// to is by construction a list no human has edited, and "IaR changed its mind
// about someone between sweeps" is the only thing imported-wins can overwrite —
// which is exactly what it should overwrite.
const mergeImportedResponses = (current = {}, incoming = {}) => {
    // Where this import put each member. A uid should only ever appear in one
    // incoming list (the caller classifies each response once); if it somehow
    // appears twice, the last list wins and it still lands in exactly one.
    const claimed = {};
    // Ignore claims a member too, removing them from every attendance list.
    [...LISTS, 'ignore'].forEach(list => (incoming[list] || []).forEach(uid => { claimed[uid] = list; }));

    return LISTS.reduce((result, list) => {
        const merged = uniq([...(current[list] || []), ...(incoming[list] || [])]);
        result[list] = merged.filter(uid => !claimed[uid] || claimed[uid] === list);
        return result;
    }, {});
};

// Turn a batch of `{user, response}` rows — the shape both the ImportDialog and
// the poller hold after matching IaR names to members — into the incoming
// classifications `mergeImportedResponses` expects. Rows with no matched user are dropped
// here; callers that care about them (the poller parks them on the incident)
// track them separately before calling this.
const groupResponsesByList = (responses = [], ignoredResponses = DEFAULT_IAR_IGNORED_RESPONSES) => {
    const result = {responders: [], standby: [], cancelled: [], ignore: []};

    for (const item of responses) {
        const uid = item?.user?.uid || item?.user || null;
        if (!uid || typeof uid !== 'string') continue;
        result[classifyIarResponse(item.response, ignoredResponses)].push(uid);
    }

    return result;
};

module.exports = {
    DEFAULT_IAR_IGNORED_RESPONSES,
    RESPONSE_LISTS: LISTS,
    classifyIarResponse,
    latestIarResponses,
    iarResponseSnapshot,
    groupResponsesByList,
    mergeImportedResponses
};
